import {
  gitlabAiAccessTokenPrefix,
  gitlabAiAdminTokenSecretId,
  gitlabAiConnectorName,
} from "@p0security/integrations-gitlab-ai-shared/secret-manager";
import type { GitlabAiComponents } from "@p0security/integrations-gitlab-ai-shared/types";
import type { FrontendInstallContext } from "@p0security/integrations-installer";
import {
  awsConnectorTf,
  gcpConnectorTf,
  p0InvokerEmail,
  p0InvokerRoleName,
  secretProviderFor,
  shellLines,
  tfId,
} from "@p0security/integrations-vaulted-credential-shared";
import type { ItemConfigOf } from "@p0security/shared/install/types";

type SecretManagerConfig = ItemConfigOf<
  GitlabAiComponents["credential"]
>["secretManager"];

export type InstallContext = FrontendInstallContext<unknown>;

const GCP_CONNECTOR_IMAGE = {
  imageName: "docker.io/p0security/p0-connector-gitlab-ai-gcloud",
  digest:
    "sha-af8ab2b@sha256:f540a1cb0c4768375cbc0a0dae7c19c8677d1107b4e054ae4466429789c37dbe",
} as const;

const AWS_CONNECTOR_IMAGE = {
  imageName: "p0security/p0-connector-gitlab-ai-aws",
  digest:
    "sha-9f3e212@sha256:ae9b5b05c76b0c3a5b2f3236fec1f396504d7bcecbe441d99be1d9ed4c5b4a90",
} as const;

const awsAdminTokenSecretId = (group: string) =>
  secretProviderFor("aws").replacer(gitlabAiAdminTokenSecretId(group));

export const awsStoreAdminToken = (
  group: string,
  secretManager: Extract<SecretManagerConfig, { type: "aws-sm" }>
) =>
  shellLines(
    `printf '%s' "$GITLAB_TOKEN"`,
    "| aws secretsmanager put-secret-value",
    `--secret-id ${awsAdminTokenSecretId(group)}`,
    `--region ${secretManager.secretsRegion}`,
    "--secret-string file:///dev/stdin"
  );

export const awsTf = (
  context: InstallContext,
  itemId: string,
  secretManager: Extract<SecretManagerConfig, { type: "aws-sm" }>
) =>
  awsConnectorTf({
    names: { resource: "gitlab", label: "GitLab" },
    image: AWS_CONNECTOR_IMAGE,
    accountId: secretManager.install,
    region: secretManager.connectorRegion,
    secretsRegion: secretManager.secretsRegion,
    connectorName: gitlabAiConnectorName,
    p0RoleName: p0InvokerRoleName(context),
    tokenSecretPrefix: secretProviderFor("aws").replacer(
      gitlabAiAccessTokenPrefix(itemId)
    ),
    installSecret: {
      resource: "admin_token",
      itemTfId: tfId(itemId),
      secretId: awsAdminTokenSecretId(itemId),
      label: "admin token",
    },
  });

const gcpAdminTokenSecretId = (group: string) =>
  secretProviderFor("gcp").replacer(gitlabAiAdminTokenSecretId(group));

export const gcpStoreAdminToken = (
  group: string,
  secretManager: Extract<SecretManagerConfig, { type: "gcp-sm" }>
) =>
  shellLines(
    `printf '%s' "$GITLAB_TOKEN"`,
    `| gcloud secrets versions add ${gcpAdminTokenSecretId(group)}`,
    `--project=${secretManager.install}`,
    "--data-file=-"
  );

export const gcpTf = (
  context: InstallContext,
  itemId: string,
  secretManager: Extract<SecretManagerConfig, { type: "gcp-sm" }>
) =>
  gcpConnectorTf({
    names: { resource: "gitlab", role: "GitLab", label: "GitLab" },
    image: GCP_CONNECTOR_IMAGE,
    projectId: secretManager.install,
    region: secretManager.connectorRegion,
    connectorName: secretManager.connectorServiceName,
    serviceAccountId:
      secretManager.connectorServiceAccount?.split("@")[0] ?? "",
    invokerEmail: p0InvokerEmail(context.installed),
    tokenSecretPrefix: secretProviderFor("gcp").replacer(
      gitlabAiAccessTokenPrefix(itemId)
    ),
    installSecret: {
      resource: "admin_token",
      itemTfId: tfId(itemId),
      secretId: gcpAdminTokenSecretId(itemId),
      label: "admin token",
    },
  });
